import {
  ChangeDetectionStrategy,
  Component,
  ViewChild,
  computed,
  effect,
  forwardRef,
  input,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { Color } from 'src/app/shared/types/Color.model';
import { ChipComponent } from 'src/app/ui-kit/chip/chip.component';
import { IconComponent } from 'src/app/ui-kit/icon/icon.component';
import { InputComponent } from 'src/app/ui-kit/form-controls/input/input.component';

/**
 * Free-text "tag input": user types into an `<app-input>` and presses Enter
 * to commit each entry as a chip. The form-control value is a `string[]`.
 *
 * Differs from `<app-chip-list>`, which is multi-select over a fixed set of
 * predefined options. Use `<app-tag-input>` when the values aren't known
 * up front (e.g. school names, free-form tags, email recipient lists).
 *
 * Implements `ControlValueAccessor` so it slots into both reactive and
 * template-driven forms.
 *
 * ```html
 * <app-tag-input
 *   formControlName="schoolNames"
 *   label="Schools"
 *   helperText="Press Enter to add another."
 *   placeholder="e.g. Lincoln Elementary"
 * ></app-tag-input>
 * ```
 *
 * @feature shared
 */
@Component({
  selector: 'app-tag-input',
  templateUrl: './tag-input.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block w-full' },
  imports: [CommonModule, ReactiveFormsModule, InputComponent, ChipComponent, IconComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TagInputComponent),
      multi: true,
    },
  ],
})
export class TagInputComponent implements ControlValueAccessor {
  // ── Inputs ───────────────────────────────────────────────────────────────

  /** Optional label rendered above the input via `<app-input>`. */
  readonly label = input<string | undefined>(undefined);

  /** Native input placeholder. */
  readonly placeholder = input<string | undefined>(undefined);

  /** Helper text rendered below the input via `<app-input>`. */
  readonly helperText = input<string | undefined>(undefined);

  /** Required marker on the label. Visual only — pair with a Validator. */
  readonly required = input<boolean>(false);

  /** `name` attribute, forwarded to the inner input. */
  readonly name = input<string | undefined>(undefined);

  /** Color theme for each chip. @default 'surface-medium' */
  readonly chipColor = input<Color>('surface-medium');

  /** Whether the chip row renders above or below the input. @default 'below' */
  readonly chipsPosition = input<'above' | 'below'>('below');

  /**
   * Whether dedupe should respect letter case. When false (default), typing
   * "lincoln" after "Lincoln" is treated as a duplicate and silently dropped.
   */
  readonly caseSensitive = input<boolean>(false);

  /** Optional cap on how many chips can be added. */
  readonly maxChips = input<number | undefined>(undefined);

  /**
   * When true, leaving the field (blur) auto-commits any text still in the
   * input as a chip. Off by default to keep the model deliberate — users
   * who type without pressing Enter shouldn't be surprised by ghost chips.
   */
  readonly addOnBlur = input<boolean>(false);

  /** Programmatic setter for chips (alternative to ngModel/formControl). */
  readonly value = input<string[]>([]);

  // ── Outputs ──────────────────────────────────────────────────────────────

  /** Emitted with the chip text after a chip is committed. */
  readonly chipAdded = output<string>();

  /** Emitted with the chip text after a chip is removed. */
  readonly chipRemoved = output<string>();

  // ── Internal state ───────────────────────────────────────────────────────

  /** Internal canonical chip list — mirrors the form-control value. */
  readonly chips = signal<string[]>([]);

  /** Disabled state set via the form or programmatically. */
  readonly isDisabled = signal(false);

  /**
   * Local `FormControl` driving the inner `<app-input>`. Lives independently
   * of the outer (consumer-supplied) control because the outer control's
   * value is `string[]`, not `string`. Cleared on every successful commit.
   */
  readonly inputControl = new FormControl<string>('', { nonNullable: true });

  /** Reference to the inner `<app-input>` so we can force-clear + refocus. */
  @ViewChild(InputComponent)
  private innerInput?: InputComponent;

  /** True when adding another chip would exceed `maxChips`. */
  readonly atMaxChips = computed(() => {
    const max = this.maxChips();
    return max !== undefined && this.chips().length >= max;
  });

  /**
   * Live mirror of the inner input's text, as a signal, so the template can
   * react to text the user has typed but not yet committed with Enter. Drives
   * the "not added yet" indicator below the field.
   */
  private readonly pendingText = toSignal(this.inputControl.valueChanges, {
    initialValue: this.inputControl.value,
  });

  /** Trimmed pending text — empty string when nothing is staged. */
  readonly trimmedPending = computed(() => (this.pendingText() ?? '').trim());

  /** True when the input holds non-empty text that isn't a committed chip yet. */
  readonly hasPendingText = computed(() => this.trimmedPending().length > 0);

  /** True when the pending text matches an existing chip (so committing is a no-op). */
  readonly pendingIsDuplicate = computed(() => {
    const name = this.trimmedPending();
    if (!name) return false;
    return this.caseSensitive()
      ? this.chips().includes(name)
      : this.chips().some(c => c.toLowerCase() === name.toLowerCase());
  });

  /**
   * True when the pending text can actually be committed — drives the in-field
   * "+" overlay. False for empty, duplicate, or at-max so we never offer an
   * add that `addChip` would silently reject.
   */
  readonly hasCommittablePending = computed(
    () => this.hasPendingText() && !this.pendingIsDuplicate() && !this.atMaxChips(),
  );

  /**
   * True once Angular forms has called `writeValue` on this component. Used
   * by `_syncValue` so the initial `value()` default of `[]` doesn't
   * overwrite a form-driven hydration that happened first.
   */
  private hasWriteValue = false;

  /**
   * Sync the `value` input to the internal chips signal whenever the input
   * changes. Re-runs ONLY when `value()` changes (chips is never read inside
   * the effect, so user clicks don't loop). Skipped when the form control
   * has already hydrated us and the input is at its default empty state —
   * otherwise the effect's initial pass with `value() === []` would wipe
   * the form's pre-populated value.
   */
  private readonly _syncValue = effect(() => {
    const v = this.value();
    if (this.hasWriteValue && v.length === 0) return;
    this.chips.set(v);
  });

  // ── ControlValueAccessor ─────────────────────────────────────────────────

  private onChange: (value: string[]) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: string[]): void {
    this.hasWriteValue = true;
    this.chips.set(Array.isArray(value) ? value : []);
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
    if (isDisabled) {
      this.inputControl.disable({ emitEvent: false });
    } else {
      this.inputControl.enable({ emitEvent: false });
    }
  }

  // ── Behavior ─────────────────────────────────────────────────────────────

  /**
   * Enter key handler. Commits the current input value as a chip, clears
   * the input, and re-focuses it so the user can keep typing chip-by-chip.
   */
  onEnter(): void {
    const raw = this.inputControl.value;
    if (this.addChip(raw)) this.clearInputAndRefocus();
  }

  /**
   * Backspace key handler. When the input is empty and there's at least one
   * chip, removes the most recent one — standard tag-input "undo" UX.
   */
  onBackspace(event: KeyboardEvent): void {
    const val = this.inputControl.value;
    if (val.length > 0) return;
    if (this.chips().length === 0) return;
    event.preventDefault();
    this.removeChipAt(this.chips().length - 1);
  }

  /**
   * Blur handler. Always marks the control touched; optionally commits the
   * current text as a chip when `addOnBlur` is enabled.
   */
  onBlur(): void {
    this.onTouched();
    if (!this.addOnBlur()) return;
    if (this.addChip(this.inputControl.value)) this.clearInputAndRefocus();
  }

  /** Remove the chip at `index` (called from the chip's `(removed)` output). */
  removeChipAt(index: number): void {
    if (this.isDisabled()) return;
    const current = this.chips();
    if (index < 0 || index >= current.length) return;
    const removed = current[index];
    const next = current.filter((_, i) => i !== index);
    this.chips.set(next);
    this.onChange(next);
    this.chipRemoved.emit(removed);
  }

  /**
   * Attempt to add `raw` as a new chip. Returns `true` on success so the
   * Enter / blur handlers know whether to clear the input.
   *
   * Drops empty values, values exceeding `maxChips`, and duplicates (case
   * sensitivity controlled by the `caseSensitive` input).
   */
  private addChip(raw: string): boolean {
    if (this.isDisabled()) return false;
    const name = (raw ?? '').trim();
    if (!name) return false;
    if (this.atMaxChips()) return false;

    const current = this.chips();
    const isDuplicate = this.caseSensitive()
      ? current.includes(name)
      : current.some(c => c.toLowerCase() === name.toLowerCase());
    if (isDuplicate) return false;

    const next = [...current, name];
    this.chips.set(next);
    this.onChange(next);
    this.chipAdded.emit(name);
    return true;
  }

  /**
   * Clear the inner input and restore focus. `<app-input>`'s `[value]`
   * property binding occasionally doesn't flush an empty string back to the
   * DOM (Angular caches the bound value by reference), so we patch the
   * native element directly via `getInputElement()`.
   */
  private clearInputAndRefocus(): void {
    this.inputControl.setValue('');
    this.innerInput?.getInputElement().then(el => {
      el.value = '';
      el.focus();
    });
  }
}
